import { ICellRendererParams, IHeaderParams } from "ag-grid-community";
import {
  Archive,
  ChevronsDownUp,
  ChevronsUpDown,
  Clock,
  Frown,
  Info,
} from "lucide-react";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Updater } from "use-immer";

import {
  ColorType,
  IgButton,
  IgIconButton,
  IgStatusCircle,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import {
  ConfigurationDto,
  ExperienceListDto,
  ExperienceStatus,
  PersonalizationStatusTextMap,
  ScheduleDto,
  TabEnumFe,
  WidgetRenderLocation,
} from "@intelligems/ig-types";
import { Grid, useTheme } from "@mui/material";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { TestTypeIcon } from "@src/features/experiments/components/TestTypeIcon";
import {
  isRolloutEnabled,
  onPriceTestAction,
} from "@src/features/experiments/utils/experienceActionUtils";
import { TargetingOverview } from "@src/features/sharedExperience/components/targeting/TargetingOverview";
import { onPersonalizationActionClick } from "@src/features/sharedExperience/tableUtils";
import { useIsExperienceMutating } from "@src/hooks/use-is-experience-mutating";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { AppDispatch, useDispatch } from "@src/store";
import { getDefaultHiddenEndTime } from "@src/store/helpers";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import { setDialog } from "@src/store/slices/global";
import { FoundExperimentDetails } from "@src/types/experience";
import { mpClickExperienceTableName } from "@src/utils/mixpanel";

export const SharedTargetingColumn = <T extends ExperienceListDto>(
  props: ICellRendererParams<ExperienceListDto> & {
    onGeneralChipClick: (id: string) => void;
    columnExpanded: boolean;
    data: T;
  }
) => {
  const onChipClickWrapper = () => {
    props.onGeneralChipClick(props.data.id);
  };
  return (
    <TargetingOverview
      audienceState={props.data.audience}
      experience={props.data}
      pageTargeting={props.data.experiencePageTargeting}
      chipColor={"default"}
      hideTooltip={true}
      groupLabelCategories={true}
      cutOffAt={props.columnExpanded ? 10 : 2} // 10 is arbitrary, just to show more chips when expanded
      separateExpressions={true}
      onGeneralChipClick={onChipClickWrapper}
    />
  );
};

export const SharedTargetingColumnHeader = (
  props: IHeaderParams & { columnExpanded: boolean; onExpandClick: () => void }
) => {
  const theme = useTheme();
  const { columnExpanded, onExpandClick } = props;
  return (
    <Grid
      container
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Grid item>
        <IgTypography color={theme.palette.text.secondary}>
          Targeting
        </IgTypography>
      </Grid>
      <Grid item>
        {/*<IgIconButton size={"small"} onClick={onExpandClick}>*/}
        {/*  {columnExpanded ? (*/}
        {/*    <ChevronsDownUp size={16} />*/}
        {/*  ) : (*/}
        {/*    <ChevronsUpDown size={16} />*/}
        {/*  )}*/}
        {/*</IgIconButton>*/}
      </Grid>
    </Grid>
  );
};

export const SharedTitleColumn = (
  props: ICellRendererParams<ExperienceListDto> & {
    orgConfigurations: ConfigurationDto[];
    setCurrentExperience?: Updater<ExperienceListDto | undefined>;
    storeCurrencyConfig: { code?: string; locale?: string };
  }
) => {
  const {
    data = {} as ExperienceListDto,
    orgConfigurations,
    setCurrentExperience,
    storeCurrencyConfig,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();

  if (!data || !data.id) {
    console.warn("SharedTitleColumn: Received invalid data", { data });
    return null;
  }

  const experienceType =
    data.category === "personalization" ? "" : `&type=${data.type}`;
  const navigateLink = `${data.category === "personalization" ? PERSONALIZATION_ROUTES.Personalization : EXPERIMENT_ROUTES.Experiment}/${data.id}?action=edit${experienceType}&tab=${
    data.status === "pending" ? TabEnumFe.MODS : TabEnumFe.ANALYTICS
  }`;
  const isEdit = data.status === "pending" || data.status === "paused";
  const state = isEdit ? { id: data.id } : { name: "overview" };

  const renderRolloutOption =
    data.category === "experiment" &&
    (data.status === "started" || data.status === "paused") &&
    isRolloutEnabled(
      data,
      checkBooleanScope("personalizations", orgConfigurations)
    ) &&
    data.variations.some((variation) => variation.percentage === 100) &&
    checkBooleanScope("personalizations", orgConfigurations);

  return (
    <Grid
      item
      container
      direction={"row"}
      alignItems={"center"}
      justifyContent={"flex-start"}
      columnSpacing={1}
    >
      {data.category === "experiment" && (
        <Grid item>
          <Link
            style={{ textDecoration: "none", color: "black" }}
            to={navigateLink}
            state={state}
            onClick={() => {
              if (!isEdit) {
                dispatch(
                  updateDashboardFilter({
                    key: "experienceId",
                    value: undefined,
                  })
                );
              }

              mpClickExperienceTableName(isEdit ? "edit" : "analytics");
            }}
          >
            <TestTypeIcon fontSize={"20px"} testFlowType={data.type} />
          </Link>
        </Grid>
      )}
      <Grid item xs zeroMinWidth>
        <Link
          style={{
            textDecoration: "none",
            fontWeight: 400,
          }}
          to={navigateLink}
          state={state}
          onClick={() => {
            if (!isEdit) {
              dispatch(
                updateDashboardFilter({
                  key: "experienceId",
                  value: undefined,
                })
              );
            }

            mpClickExperienceTableName(isEdit ? "edit" : "analytics");
          }}
        >
          <IgTypographyTooltip
            variant={"body1"}
            textMaxWidth={"240px"}
            text={data.name}
            textSx={{ fontWeight: 400 }}
          />
        </Link>
      </Grid>
      {renderRolloutOption ? (
        <Grid item>
          <IgTooltip
            title={
              <IgTypography sx={{ display: "block" }}>
                <IgTypography igStyle={"semibold"}>
                  Trying to Personalize?
                </IgTypography>
                <div>&nbsp;</div>
                This test is sending 100% of traffic to one group. It’s better
                to roll it out as an Experience. Experiences support the same
                changes, targeting, and analytics without the need for test
                groups.
                <div>&nbsp;</div>
                <IgTypography
                  onClick={() => {
                    if (setCurrentExperience) {
                      setCurrentExperience(data);
                    }
                    if (data.testTypes.hasTestPricing) {
                      onPriceTestAction(
                        data,
                        "end",
                        storeCurrencyConfig?.code ?? "USD",
                        dispatch
                      );
                    } else {
                      dispatch(
                        updateDashboardFilter({
                          key: "hiddenEndTime",
                          value: getDefaultHiddenEndTime(data),
                        })
                      );

                      dispatch(
                        setDialog({
                          open: true,
                          dialog: "rollouts",
                        })
                      );
                    }
                  }}
                  sx={{
                    textDecoration: "underline",
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  Roll out to Experience
                </IgTypography>
              </IgTypography>
            }
          >
            <Info
              size={16}
              color={theme.igIconColors.warning}
              style={{
                cursor: "pointer",
              }}
            />
          </IgTooltip>
        </Grid>
      ) : null}
    </Grid>
  );
};

export const SharedStatusColumn = (
  params: ICellRendererParams<ExperienceListDto> & { schedules: ScheduleDto[] }
) => {
  const { data = {} as ExperienceListDto, schedules, value } = params;
  const statusTypeColorMap: Record<ExperienceStatus | "archived", ColorType> = {
    ended: "error",
    pending: "secondary",
    paused: "secondary",
    started: "success",
    archived: "primary",
  };

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => schedule.experienceId === data.id);
  }, [schedules]);

  return (
    <Grid
      container
      direction={"row"}
      wrap={"nowrap"}
      spacing={2}
      alignItems={"center"}
    >
      <Grid item sx={{ display: "flex", alignItems: "center" }}>
        {filteredSchedules.length > 0 ? (
          <Clock size={14} />
        ) : (
          <IgStatusCircle
            color={statusTypeColorMap[data.archivedAtTs ? "archived" : value]}
          />
        )}
      </Grid>
      <Grid item>
        <IgTypography>
          {filteredSchedules.length > 0
            ? "Scheduled"
            : PersonalizationStatusTextMap[
                data.archivedAtTs ? "archived" : value
              ]}
        </IgTypography>
      </Grid>
    </Grid>
  );
};

export const SharedActionColumn = (
  params: ICellRendererParams<ExperienceListDto> & {
    hasPriceModAccess: boolean;
    hasOfferModAccess: boolean;
    inactiveSubscriptionGate: boolean;
    isUnderOfferLimit: boolean;
    isAdmin: boolean;
    isSchedulesLoading: boolean;
    schedules: ScheduleDto[];
    onActionClick: (personalization: ExperienceListDto) => void;
    getIsUsedInTest: (experienceId: string) => FoundExperimentDetails;
  }
) => {
  const {
    data = {} as ExperienceListDto,
    inactiveSubscriptionGate,
    hasOfferModAccess,
    hasPriceModAccess,
    isUnderOfferLimit,
    isAdmin,
    isSchedulesLoading,
    schedules,
    onActionClick,
    getIsUsedInTest,
  } = params;
  const dispatch = useDispatch();
  const isStartExperienceMutating = useIsExperienceMutating(
    "startExperience",
    data.id
  );
  const isUsedInTest = getIsUsedInTest(data.id);

  const isPauseExperienceMutating = useIsExperienceMutating(
    "pauseExperience",
    data.id
  );

  if (data.archivedAtTs) {
    return "";
  }

  const shouldBlockPriceStart =
    data.testTypes.hasTestPricing && !hasPriceModAccess;
  const shouldBlockOfferStart =
    data.testTypes.hasTestCampaign && !hasOfferModAccess;
  const subscriptionPlanGate =
    !isAdmin && (shouldBlockOfferStart || shouldBlockPriceStart);

  return (
    <ScopedStartExperienceWithDisabled
      experience={data}
      debugId={"site-offer-table-action-button"}
    >
      <IgTooltip
        title={
          (data.status === "pending" || data.status === "paused") &&
          inactiveSubscriptionGate
            ? "Approve billing to start this personalization."
            : (data.status === "pending" || data.status === "paused") &&
                subscriptionPlanGate
              ? "This personalization includes modifications not in your plan. Upgrade your plan to start this personalization."
              : !isUnderOfferLimit && data.testTypes.hasTestCampaign
                ? "Not enough function space for this personalization"
                : ""
        }
      >
        <IgButton
          size={"extraSmall"}
          onClick={() => {
            onPersonalizationActionClick({
              personalization: data,
              schedules,
              isUsedInTest,
              dispatch,
              onActionClick,
            });
          }}
          loading={
            isStartExperienceMutating > 0 || isPauseExperienceMutating > 0
          }
          disabled={
            isSchedulesLoading ||
            isPauseExperienceMutating > 0 ||
            isStartExperienceMutating > 0 ||
            ((data.status === "pending" || data.status === "paused") &&
              inactiveSubscriptionGate) ||
            ((data.status === "pending" || data.status === "paused") &&
              subscriptionPlanGate) ||
            ((data.status === "pending" || data.status === "paused") &&
              !isUnderOfferLimit &&
              data.testTypes.hasTestCampaign)
          }
          variant={data.status === "started" ? "text" : "outlined"}
        >
          {data.status === "started" ? "Disable" : "Enable"}
        </IgButton>
      </IgTooltip>
    </ScopedStartExperienceWithDisabled>
  );
};
